/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { observable, action, decorate, computed, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';

import jwtDecode from 'jwt-decode';

import { getTokenExpiryMs } from '../helpers/api';
import { storage } from '../helpers/utils';
import localStorageKeys from '../models/constants/local-storage-keys';

// expected props
// - authentication
// - app
class ForceLogout extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.tokenActive = true;
      this.intervalId = undefined;
      this.refreshInProgress = false;
    });
  }

  get app() {
    return this.props.app;
  }

  get authentication() {
    return this.props.authentication;
  }

  get modalOpen() {
    return !this.tokenActive;
  }

  componentDidMount() {
    const PROACTIVE_REFRESH_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

    // Only start the token check interval if the user is authenticated
    if (!this.app.userAuthenticated) return;

    runInAction(() => {
      this.intervalId = setInterval(async () => {
        if (this.refreshInProgress) return;

        if (this.hasTokenExpired()) {
          // Reactive refresh: token already expired, attempt silent refresh before showing modal
          runInAction(() => {
            this.refreshInProgress = true;
          });
          try {
            const success = await this.authentication.attemptSilentRefresh();
            runInAction(() => {
              this.refreshInProgress = false;
              if (!success) {
                this.stopInterval();
                this.tokenActive = false;
              }
            });
          } catch (e) {
            runInAction(() => {
              this.refreshInProgress = false;
              this.stopInterval();
              this.tokenActive = false;
            });
          }
        } else {
          // Proactive refresh: token not expired but within 10 minutes of expiry
          const expiryMs = getTokenExpiryMs();
          if (expiryMs > 0 && expiryMs - Date.now() < PROACTIVE_REFRESH_WINDOW_MS) {
            runInAction(() => {
              this.refreshInProgress = true;
            });
            try {
              await this.authentication.attemptSilentRefresh();
            } catch (e) {
              // Proactive refresh failure is non-fatal; will retry on next interval or handle reactively
            } finally {
              runInAction(() => {
                this.refreshInProgress = false;
              });
            }
          }
        }
      }, 30000); // Check every 30 seconds — ID tokens typically last 1 hour
    });
  }

  componentWillUnmount() {
    this.stopInterval();
  }

  doLogout = async () => {
    this.stopInterval();
    return this.authentication.logout({ autoLogout: true });
  };

  stopInterval() {
    if (!_.isUndefined(this.intervalId)) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  hasTokenExpired = () => {
    try {
      const idToken = localStorage.getItem('appIdToken');
      const decodedIdToken = jwtDecode(idToken);
      const expiresAt = _.get(decodedIdToken, 'exp', 0) * 1000;
      // The next line computes how many minutes are left until token expiration
      return (expiresAt - Date.now()) / 60 / 1000 < 0;
    } catch (e) {
      return false;
    }
  };

  handleLogout = async event => {
    event.preventDefault();
    event.stopPropagation();
    return this.doLogout();
  };

  getRefreshTokenStatus() {
    try {
      const refreshToken = storage.getItem(localStorageKeys.appRefreshToken);
      if (!refreshToken) return 'No refresh token stored';
      // Cognito refresh tokens are opaque (not JWT), so we can't decode expiry from them.
      return 'Refresh token was present but refresh failed';
    } catch (e) {
      return 'Unable to read refresh token';
    }
  }

  renderModal() {
    if (this.tokenActive) {
      return null;
    }

    const refreshStatus = this.getRefreshTokenStatus();
    let idTokenExpiry = 'Unknown';
    try {
      const idToken = localStorage.getItem('appIdToken');
      if (idToken) {
        const decoded = jwtDecode(idToken);
        if (decoded.exp) {
          idTokenExpiry = new Date(decoded.exp * 1000).toLocaleString();
        }
      }
    } catch (e) {
      // ignore
    }

    return (
      <div className="rmit-modal-overlay">
        <div className="rmit-modal">
          <div className="rmit-modal-icon rmit-modal-icon-error">
            <i className="exclamation triangle icon" />
          </div>
          <h2 className="rmit-modal-title">Session Expired</h2>
          <p className="rmit-modal-text">Your session has expired. Please log out and sign in again to continue.</p>
          <p className="rmit-modal-text" style={{ fontSize: '0.85em', opacity: 0.7 }}>
            ID token expired: {idTokenExpiry}<br />
            Refresh status: {refreshStatus}
          </p>
          <div className="rmit-modal-actions">
            <button className="rmit-modal-btn rmit-modal-btn-primary" onClick={this.handleLogout}>
              Log Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const authenticated = this.app.userAuthenticated;
    if (!authenticated) return null;
    return <>{this.renderModal()}</>;
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(ForceLogout, {
  app: computed,
  authentication: computed,
  modalOpen: computed,
  tokenActive: observable,
  intervalId: observable,
  refreshInProgress: observable,
  doLogout: action,
  handleLogout: action,
  stopInterval: action,
});

export default inject('authentication', 'app')(observer(ForceLogout));
